import {
  Box, Button, Card, CardContent, Chip, FormControlLabel, Grid, MenuItem,
  Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { practice } from '@/api/endpoints';

type PracticeProfile = {
  id: number;
  practice_type: string;
  public_name: string;
  slug: string;
  tagline: string;
  description: string;
  primary_jurisdiction: { id: number; code: string; display_name: string };
  languages: string[];
  accepts_insurance: boolean;
  accepted_insurers: string[];
  is_listed: boolean;
  is_verified: boolean;
};

const PRACTICE_TYPES = [
  { code: 'solo',       label: 'Solo practice' },
  { code: 'clinic',     label: 'Clinic / medical center' },
  { code: 'hospital',   label: 'Hospital department' },
  { code: 'telehealth', label: 'Telehealth-only' },
  { code: 'mobile',     label: 'Mobile / home visits' },
  { code: 'pharmacy',   label: 'Pharmacy' },
];

export function PracticeProfilePage() {
  const q = useApiQuery({
    queryKey: ['practice', 'me'],
    queryFn:  practice.me.get as () => Promise<PracticeProfile>,
  });

  return (
    <>
      <PageHeader title="Practice profile" />
      <DataState query={q}>
        {(p) => <ProfileForm initial={p} />}
      </DataState>
    </>
  );
}

function ProfileForm({ initial }: { initial: PracticeProfile }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [publicName, setPublicName] = useState(initial.public_name);
  const [tagline, setTagline] = useState(initial.tagline);
  const [description, setDescription] = useState(initial.description);
  const [practiceType, setPracticeType] = useState(initial.practice_type);
  const [languages, setLanguages] = useState((initial.languages ?? []).join(', '));
  const [acceptsInsurance, setAcceptsInsurance] = useState(initial.accepts_insurance);
  const [insurers, setInsurers] = useState((initial.accepted_insurers ?? []).join(', '));
  const [isListed, setIsListed] = useState(initial.is_listed);

  useEffect(() => {
    setPublicName(initial.public_name);
    setTagline(initial.tagline);
    setDescription(initial.description);
    setPracticeType(initial.practice_type);
    setLanguages((initial.languages ?? []).join(', '));
    setAcceptsInsurance(initial.accepts_insurance);
    setInsurers((initial.accepted_insurers ?? []).join(', '));
    setIsListed(initial.is_listed);
  }, [initial.id]);

  const saveMut = useApiMutation({
    mutationFn: (patch: Record<string, unknown>) => practice.me.update(patch),
    onSuccess: () => {
      toast.success('Practice profile saved.');
      qc.invalidateQueries({ queryKey: ['practice', 'me'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const submit = () => {
    saveMut.mutate({
      public_name: publicName,
      tagline,
      description,
      practice_type: practiceType,
      languages: languages.split(',').map((s) => s.trim()).filter(Boolean),
      accepts_insurance: acceptsInsurance,
      accepted_insurers: insurers.split(',').map((s) => s.trim()).filter(Boolean),
      is_listed: isListed,
    });
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Public profile
            </Typography>
            {initial.is_verified && (
              <Chip size="small" color="success" variant="outlined" label="verified" />
            )}
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField fullWidth required label="Public name"
                         value={publicName}
                         onChange={(e) => setPublicName(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Slug" value={initial.slug} disabled
                         helperText="Slug cannot be changed after the practice is verified." />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth select label="Type"
                         value={practiceType}
                         onChange={(e) => setPracticeType(e.target.value)}>
                {PRACTICE_TYPES.map((t) => (
                  <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Languages"
                         helperText="Comma-separated. e.g. en, pl, de"
                         value={languages}
                         onChange={(e) => setLanguages(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Tagline"
                         placeholder="One-sentence summary patients see in search."
                         value={tagline}
                         onChange={(e) => setTagline(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={4} label="Description"
                         value={description}
                         onChange={(e) => setDescription(e.target.value)} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1.5 }}>
            Insurance
          </Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={<Switch checked={acceptsInsurance}
                               onChange={(_, v) => setAcceptsInsurance(v)} />}
              label="Accept insurance" />
            {acceptsInsurance && (
              <TextField fullWidth label="Accepted insurers"
                         helperText="Comma-separated insurer codes."
                         value={insurers}
                         onChange={(e) => setInsurers(e.target.value)} />
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1.5 }}>
            Visibility
          </Typography>
          <FormControlLabel
            control={<Switch checked={isListed} onChange={(_, v) => setIsListed(v)} />}
            label="Listed in marketplace search" />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Practice listing requires at least one verified clinician with an active license.
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={submit} disabled={saveMut.isPending}>
          Save profile
        </Button>
      </Box>
    </Stack>
  );
}
